// client/src/components/shared/BrecAppGuide.js — how to install the BREC app on each
// device. Used by the public page /app (the link the office hands out) and by
// My Office -> The BREC App in the dashboard.
import React from 'react';
import { InstallAppButton, IosSteps, useInstallMode } from './InstallApp';

const Card = ({ title, children }) => (
    <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 sm:p-5">
        <h3 className="text-base font-semibold text-slate-800 mb-2">{title}</h3>
        <div className="text-sm text-slate-700 space-y-2">{children}</div>
    </section>
);

const BrecAppGuide = ({ apkAvailable = null }) => {
    const mode = useInstallMode();
    return (
        <div className="space-y-4">
            {mode === 'installed' && (
                <div className="rounded-md bg-green-50 border border-green-200 text-green-900 text-sm px-4 py-3">You are using the installed app on this device.</div>
            )}
            {(mode === 'prompt' || mode === 'ios') && (
                <div className="flex flex-wrap items-center gap-3 rounded-md bg-indigo-50 border border-indigo-200 px-4 py-3 text-sm text-indigo-900">
                    <span>This device can install the app now:</span>
                    <InstallAppButton />
                </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
                <Card title="iPhone and iPad">
                    <p>Open the portal in <strong>Safari</strong>, then: <IosSteps /></p>
                    <p>Tap <strong>Add</strong>. The BREC icon appears on the home screen and opens the portal full-screen.</p>
                    <p className="text-xs text-slate-500">In Chrome or another browser on iPhone, use its Share button the same way (iOS 16.4 and later), or switch to Safari.</p>
                </Card>
                <Card title="Android phone or tablet">
                    <p><strong>Option 1 — from Chrome (recommended):</strong> open the portal, tap <strong>Install</strong> when it is offered (or menu ⋮ → <strong>Install app</strong> / <strong>Add to Home screen</strong>).</p>
                    <p><strong>Option 2 — the BREC Android app:</strong> {apkAvailable === false
                        ? <>not published yet — use option 1.</>
                        : <>download <a className="text-indigo-700 underline" href="/app/android">the app file (.apk)</a>, open it, and allow "install unknown apps" for your browser when Android asks. It opens the live portal, so it is always up to date.</>}
                    </p>
                </Card>
                <Card title="Windows or Mac computer">
                    <p>In <strong>Chrome</strong> or <strong>Edge</strong>, open the portal and click the install icon at the right end of the address bar (or menu → <strong>Apps</strong> / <strong>Cast, save and share</strong> → <strong>Install</strong>).</p>
                    <p>On a Mac with Safari (macOS 14+): <strong>File → Add to Dock</strong>.</p>
                    <p>The app gets its own window and a Start-menu / Dock icon.</p>
                </Card>
                <Card title="Updates and privacy">
                    <p>There is nothing to update by hand. When the office publishes a new version, the app picks it up the next time it is opened; if it is already open, a bar appears: <strong>A new version is ready — Reload</strong>.</p>
                    <p>Nothing private is stored on the device — clients, deals, documents and money always come from the server. With no connection the app says it is offline.</p>
                </Card>
            </div>
        </div>
    );
};

export default BrecAppGuide;
